import * as THREE from 'three';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import {
  createGbaModel,
  getGbaRuntime,
  type GbaControlId,
  type GbaSculptRuntime,
} from '../models/GbaModel';
import {
  createGamePackage,
  type GamePackageModel,
} from '../models/GamePackage';
import { createEnvironment, type EnvironmentModel } from '../models/Environment';
import {
  PlatformerEngine,
  type PlatformerEvent,
  type PlatformerIntent,
  type PlatformerPhase,
} from '../platformer';
import { createLightingRig } from '../systems/LightingRig';
import { RetroAudio, type RetroSoundEvent } from '../systems/RetroAudio';
import { ShowcaseCamera } from '../systems/ShowcaseCamera';

type BoxPhase = 'closed' | 'opening' | 'open';
type CartridgePhase =
  | 'cradle'
  | 'revealing'
  | 'loose'
  | 'dragging'
  | 'dropping'
  | 'inserting'
  | 'inserted';

type PickTarget =
  | { kind: 'control'; control: GbaControlId }
  | { kind: 'box' }
  | { kind: 'cartridge' }
  | { kind: 'none' };

type ActivePointer =
  | { kind: 'orbit' }
  | { kind: 'control'; control: GbaControlId; source: string }
  | { kind: 'cartridge' };

type Pose = {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  scale: THREE.Vector3;
};

type ProjectedPoint = {
  x: number;
  y: number;
  visible: boolean;
};

export interface PocketArchiveTestApi {
  setPower(on: boolean): void;
  openBox(): void;
  revealCartridge(): void;
  placeCartridgeAtSlot(): void;
  insertCartridge(): void;
  removeCartridge(): void;
  setIntent(intent: PlatformerIntent, pressed: boolean, source?: string): void;
  pulseIntent(intent: PlatformerIntent): void;
  advancePlatformer(stepCount: number): void;
  getScreenCanvas(): HTMLCanvasElement;
  getTargets(): Record<string, ProjectedPoint>;
};

const CONTROL_TO_INTENT: Partial<Record<GbaControlId, PlatformerIntent>> = {
  'dpad-up': 'up',
  'dpad-down': 'down',
  'dpad-left': 'left',
  'dpad-right': 'right',
  a: 'jump',
  b: 'run',
  start: 'start',
  select: 'select',
  l: 'run',
  r: 'jump',
};

const KEY_TO_CONTROL: Readonly<Record<string, GbaControlId>> = {
  ArrowLeft: 'dpad-left',
  KeyA: 'dpad-left',
  ArrowRight: 'dpad-right',
  KeyD: 'dpad-right',
  ArrowUp: 'dpad-up',
  KeyW: 'dpad-up',
  ArrowDown: 'dpad-down',
  KeyS: 'dpad-down',
  KeyZ: 'a',
  KeyJ: 'a',
  Space: 'a',
  KeyX: 'b',
  KeyK: 'b',
  ShiftLeft: 'b',
  ShiftRight: 'b',
  Enter: 'start',
  NumpadEnter: 'start',
  Backspace: 'select',
  Tab: 'select',
};

const SOUND_BY_PLATFORMER_EVENT: Partial<Record<PlatformerEvent['type'], RetroSoundEvent>> = {
  'boot-logo': 'boot',
  jump: 'jump',
  coin: 'coin',
  'block-hit': 'block',
  stomp: 'stomp',
  damage: 'damage',
  death: 'death',
  pause: 'pause',
  resume: 'pause',
  'level-complete': 'complete',
  victory: 'complete',
};

const Y_AXIS = new THREE.Vector3(0, 1, 0);
const CONSOLE_TABLE_X = -2.9;
const PACKAGE_TABLE_X = 6.1;

function clamp01(value: number): number {
  return THREE.MathUtils.clamp(value, 0, 1);
}

function smoothstep(value: number): number {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
}

function easeOutCubic(value: number): number {
  const t = clamp01(value);
  return 1 - (1 - t) ** 3;
}

function isDescendant(object: THREE.Object3D, ancestor: THREE.Object3D): boolean {
  let current: THREE.Object3D | null = object;
  while (current) {
    if (current === ancestor) return true;
    current = current.parent;
  }
  return false;
}

function findControlId(object: THREE.Object3D): GbaControlId | null {
  let current: THREE.Object3D | null = object;
  while (current) {
    const id = current.userData.controlId as GbaControlId | undefined;
    if (id) return id;
    current = current.parent;
  }
  return null;
}

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(42, 1, 0.1, 60);
  private readonly cameraRig = new ShowcaseCamera(this.camera);
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointerNdc = new THREE.Vector2();
  private readonly audio = new RetroAudio();
  private readonly environment: EnvironmentModel;
  private readonly lighting: THREE.Group;
  private readonly platformer: PlatformerEngine;
  private readonly screenTexture: THREE.CanvasTexture;
  private readonly gbaRoot: THREE.Group;
  private readonly gba: GbaSculptRuntime;
  private readonly controlHitMaterial: THREE.MeshBasicMaterial;
  private readonly gamePackage: GamePackageModel;
  private readonly loop: Loop;

  private readonly stateLabel: HTMLElement;
  private readonly stateStrip: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly screenReaderStatus: HTMLElement;
  private readonly audioToggle: HTMLButtonElement;
  private readonly cameraReset: HTMLButtonElement;
  private readonly helpToggle: HTMLButtonElement;
  private readonly helpPanel: HTMLElement;
  private readonly helpClose: HTMLButtonElement;

  private readonly activePointers = new Map<number, ActivePointer>();
  private readonly pressedKeyboardControls = new Set<GbaControlId>();
  private readonly dragPlane = new THREE.Plane(Y_AXIS, -0.34);
  private readonly dragOffset = new THREE.Vector3();
  private readonly dragTargetPosition = new THREE.Vector3();
  private readonly dragTargetQuaternion = new THREE.Quaternion();
  private readonly cartStartPose: Pose = {
    position: new THREE.Vector3(),
    quaternion: new THREE.Quaternion(),
    scale: new THREE.Vector3(1, 1, 1),
  };
  private readonly cartEndPose: Pose = {
    position: new THREE.Vector3(),
    quaternion: new THREE.Quaternion(),
    scale: new THREE.Vector3(1, 1, 1),
  };

  private frame = 0;
  private elapsed = 0;
  private fps = 60;
  private fpsWindowTime = 0;
  private fpsWindowFrames = 0;
  private powerOn = false;
  private boxPhase: BoxPhase = 'closed';
  private boxProgress = 0;
  private boxOpenDelay = 0;
  private cartridgePhase: CartridgePhase = 'cradle';
  private cartridgeProgress = 0;
  private dropStartY = 0.34;
  private cartCanInsert = false;
  private cartAssist = 0;
  private hovered: PickTarget = { kind: 'none' };
  private lastPlatformerPhase: PlatformerPhase = 'off';
  private lastUiSignature = '';
  private disposed = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.toneMappingExposure = 1.08;

    this.scene.background = new THREE.Color('#08070d');
    this.scene.fog = new THREE.FogExp2('#0b0912', 0.024);

    this.environment = createEnvironment();
    this.scene.add(this.environment.root);
    this.lighting = createLightingRig();
    this.scene.add(this.lighting);

    this.platformer = new PlatformerEngine({
      enableKeyboard: true,
      onEvent: (event) => this.handlePlatformerEvent(event),
    });
    this.screenTexture = new THREE.CanvasTexture(this.platformer.screenCanvas);
    this.screenTexture.name = 'live-platformer-screen';
    this.screenTexture.colorSpace = THREE.SRGBColorSpace;
    this.screenTexture.magFilter = THREE.NearestFilter;
    this.screenTexture.minFilter = THREE.NearestFilter;
    this.screenTexture.generateMipmaps = false;
    this.screenTexture.anisotropy = 1;

    this.gbaRoot = createGbaModel({
      screenTexture: this.screenTexture,
      poweredOn: false,
      scale: 0.62,
    });
    // The reference shell is slim. Compress only the face-normal axis so the
    // tabletop silhouette stays exact while the front/rear stack loses its toy-like bulk.
    this.gbaRoot.scale.set(0.62, 0.62, 0.5);
    this.gbaRoot.position.set(CONSOLE_TABLE_X, 0.68, 0.62);
    this.gbaRoot.rotation.set(-Math.PI / 2, 0, -0.018);
    this.scene.add(this.gbaRoot);
    this.gba = getGbaRuntime(this.gbaRoot);
    this.controlHitMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      colorWrite: false,
    });
    this.installControlHitTargets();
    this.raycaster.layers.enable(1);

    this.gamePackage = createGamePackage({
      textureAnisotropy: Math.min(8, this.renderer.capabilities.getMaxAnisotropy()),
    });
    this.gamePackage.root.position.set(PACKAGE_TABLE_X, 0.2, -0.62);
    this.gamePackage.root.rotation.set(-Math.PI / 2, 0, 0.025);
    this.gamePackage.root.scale.setScalar(0.72);
    // The retail case is almost as wide as the hardware. Scale the hinged case
    // independently while compensating the cartridge so it keeps true GBA scale.
    this.gamePackage.box.root.scale.setScalar(2);
    this.gamePackage.cartridge.root.scale.setScalar(0.82);
    const cartridgeHitTarget = new THREE.Mesh(
      new THREE.BoxGeometry(2.94, 1.92, 0.46),
      this.controlHitMaterial,
    );
    cartridgeHitTarget.name = 'hit-target:cartridge';
    cartridgeHitTarget.position.z = 0.28;
    cartridgeHitTarget.layers.set(1);
    cartridgeHitTarget.castShadow = false;
    cartridgeHitTarget.receiveShadow = false;
    this.gamePackage.cartridge.root.add(cartridgeHitTarget);
    this.scene.add(this.gamePackage.root);

    this.stateLabel = this.getElement('#state-label');
    this.stateStrip = this.getElement('#state-strip');
    this.hint = this.getElement('#interaction-hint');
    this.screenReaderStatus = this.getElement('#screen-reader-status');
    this.audioToggle = this.getElement<HTMLButtonElement>('#audio-toggle');
    this.cameraReset = this.getElement<HTMLButtonElement>('#camera-reset');
    this.helpToggle = this.getElement<HTMLButtonElement>('#help-toggle');
    this.helpPanel = this.getElement('#help-panel');
    this.helpClose = this.getElement<HTMLButtonElement>('#help-close');

    this.bindEvents();
    this.cameraRig.reset();
    resizeRenderer(this.renderer, this.camera, 2);
    this.cameraRig.setViewportAspect(this.canvas.clientWidth / Math.max(1, this.canvas.clientHeight));
    this.cameraRig.update(1);
    this.camera.updateMatrixWorld(true);
    this.updateUi(true);
    this.publishDiagnostics();

    this.loop = new Loop(
      (delta) => this.update(delta),
      () => this.render(),
    );

    if (import.meta.env.DEV) this.installTestApi();
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.loop.stop();
    this.releaseAllInputs();
    this.unbindEvents();
    this.platformer.dispose();
    this.audio.dispose();
    this.gba.dispose();
    this.controlHitMaterial.dispose();
    this.screenTexture.dispose();
    this.disposeObject(this.gamePackage.root);
    this.disposeObject(this.environment.root);
    this.disposeObject(this.lighting);
    const disposeEnvironment = this.environment.root.userData.dispose as (() => void) | undefined;
    disposeEnvironment?.();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__POCKET_ARCHIVE_TEST__ = undefined;
  }

  private update(delta: number): void {
    this.frame += 1;
    this.elapsed += delta;
    this.fpsWindowTime += delta;
    this.fpsWindowFrames += 1;
    if (this.fpsWindowTime >= 0.5) {
      this.fps = this.fpsWindowFrames / this.fpsWindowTime;
      this.fpsWindowTime = 0;
      this.fpsWindowFrames = 0;
    }
    resizeRenderer(this.renderer, this.camera, 2);
    this.cameraRig.setViewportAspect(this.canvas.clientWidth / Math.max(1, this.canvas.clientHeight));

    this.updateBox(delta);
    this.updateCartridge(delta);
    this.gba.update(delta);
    this.platformer.update(delta);
    if (this.platformer.consumeTextureDirty()) this.screenTexture.needsUpdate = true;

    const screenBounce = this.lighting.getObjectByName('screenBounce') as THREE.RectAreaLight | undefined;
    if (screenBounce) {
      const target = this.powerOn ? (this.platformer.state === 'playing' ? 2.4 : 1.7) : 0.18;
      screenBounce.intensity = THREE.MathUtils.lerp(
        screenBounce.intensity,
        target,
        1 - Math.exp(-5 * delta),
      );
    }

    this.cameraRig.update(delta);
    // Target projections are published before render; refresh the camera matrix
    // now so pointer/touch coordinates match the visible frame even at low FPS.
    this.camera.updateMatrixWorld(true);
    this.updateUi();
    this.publishDiagnostics();
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private updateBox(delta: number): void {
    if (this.boxPhase === 'opening') {
      this.boxProgress = clamp01(this.boxProgress + delta / 1.08);
      this.gamePackage.box.setOpenProgress(this.boxProgress);
      // The authored action opens toward +Z. The display case is face-up, so invert
      // the same physical hinge travel to keep the lid above the tabletop.
      this.gamePackage.box.lidPivot.rotation.x = -smoothstep(this.boxProgress)
        * this.gamePackage.box.openAngle;
      if (this.boxProgress >= 1) {
        this.boxPhase = 'open';
        this.boxOpenDelay = 0.22;
        this.announce('The game case is open. The cartridge is released onto the table.');
      }
    }

    if (this.boxPhase === 'open' && this.cartridgePhase === 'cradle') {
      this.boxOpenDelay -= delta;
      if (this.boxOpenDelay <= 0) this.beginCartridgeReveal();
    }
  }

  private updateCartridge(delta: number): void {
    const cartridge = this.gamePackage.cartridge.root;

    if (this.cartridgePhase === 'revealing') {
      this.cartridgeProgress = clamp01(this.cartridgeProgress + delta / 1.05);
      const t = easeOutCubic(this.cartridgeProgress);
      cartridge.position.lerpVectors(this.cartStartPose.position, this.cartEndPose.position, t);
      cartridge.position.y += Math.sin(t * Math.PI) * 1.05;
      cartridge.quaternion.copy(this.cartStartPose.quaternion).slerp(this.cartEndPose.quaternion, t);
      cartridge.scale.lerpVectors(this.cartStartPose.scale, this.cartEndPose.scale, t);
      if (this.cartridgeProgress >= 1) {
        cartridge.position.copy(this.cartEndPose.position);
        cartridge.quaternion.copy(this.cartEndPose.quaternion);
        cartridge.scale.copy(this.cartEndPose.scale);
        this.cartridgePhase = 'loose';
        this.gamePackage.cartridge.setInteractionState('idle');
        this.audio.play('drop-cartridge');
        this.announce('Cartridge ready. Drag it to the slot at the rear of the console.');
      }
    } else if (this.cartridgePhase === 'dragging') {
      const positionDamping = 1 - Math.exp(-24 * delta);
      const rotationDamping = 1 - Math.exp(-18 * delta);
      cartridge.position.lerp(this.dragTargetPosition, positionDamping);
      cartridge.quaternion.slerp(this.dragTargetQuaternion, rotationDamping);
      this.updateInsertionAssessment();
    } else if (this.cartridgePhase === 'dropping') {
      this.cartridgeProgress = clamp01(this.cartridgeProgress + delta / 0.34);
      const t = easeOutCubic(this.cartridgeProgress);
      cartridge.position.y = THREE.MathUtils.lerp(this.dropStartY, 0.34, t)
        + Math.sin(t * Math.PI) * 0.11 * (1 - t);
      if (this.cartridgeProgress >= 1) {
        cartridge.position.y = 0.34;
        this.cartridgePhase = 'loose';
        this.gamePackage.cartridge.setInteractionState('idle');
      }
    } else if (this.cartridgePhase === 'inserting') {
      this.cartridgeProgress = clamp01(this.cartridgeProgress + delta / 0.62);
      const t = smoothstep(this.cartridgeProgress);
      cartridge.position.lerpVectors(this.cartStartPose.position, this.cartEndPose.position, t);
      cartridge.quaternion.copy(this.cartStartPose.quaternion).slerp(this.cartEndPose.quaternion, t);
      cartridge.scale.lerpVectors(this.cartStartPose.scale, this.cartEndPose.scale, t);
      if (this.cartridgeProgress >= 1) {
        cartridge.position.copy(this.cartEndPose.position);
        cartridge.quaternion.copy(this.cartEndPose.quaternion);
        cartridge.scale.copy(this.cartEndPose.scale);
        this.cartridgePhase = 'inserted';
        this.gamePackage.cartridge.setInteractionState('inserted');
        this.platformer.setCartridgeInserted(true);
        this.audio.play('insert-cartridge');
        this.cameraRig.impulse(0.045);
        this.announce(
          this.powerOn
            ? 'Cartridge inserted. The console is restarting.'
            : 'Cartridge inserted. Slide the power switch to start.',
        );
      }
    } else if (this.cartridgePhase === 'inserted') {
      const seated = this.poseCartridgeAt(this.gba.cartridge.seatedSocket);
      cartridge.position.copy(seated.position);
      cartridge.quaternion.copy(seated.quaternion);
    }
  }

  private openBox(): void {
    if (this.boxPhase !== 'closed') return;
    this.boxPhase = 'opening';
    this.gamePackage.box.setHover(false);
    this.audio.play('box-open');
    this.announce('Opening the hinged game case.');
  }

  private beginCartridgeReveal(): void {
    if (this.cartridgePhase !== 'cradle') return;
    const cartridge = this.gamePackage.cartridge.root;
    cartridge.updateWorldMatrix(true, false);
    this.gamePackage.detachCartridgeTo(this.scene);
    this.capturePose(cartridge, this.cartStartPose);
    // Land fully in front of the now correctly scaled case so the pickup volume
    // never competes with the case base in the raycast.
    this.cartEndPose.position.set(PACKAGE_TABLE_X - 0.7, 0.36, 4.25);
    this.cartEndPose.quaternion.setFromEuler(new THREE.Euler(-Math.PI / 2, 0, -0.1));
    this.cartEndPose.scale.copy(this.cartStartPose.scale);
    this.cartridgeProgress = 0;
    this.cartridgePhase = 'revealing';
  }

  private revealCartridgeImmediate(): void {
    if (this.cartridgePhase !== 'cradle') return;
    this.boxPhase = 'open';
    this.boxProgress = 1;
    this.gamePackage.box.setOpenProgress(1);
    this.gamePackage.box.lidPivot.rotation.x = -this.gamePackage.box.openAngle;
    this.beginCartridgeReveal();
    const cartridge = this.gamePackage.cartridge.root;
    cartridge.position.copy(this.cartEndPose.position);
    cartridge.quaternion.copy(this.cartEndPose.quaternion);
    cartridge.scale.copy(this.cartEndPose.scale);
    this.cartridgeProgress = 1;
    this.cartridgePhase = 'loose';
    this.gamePackage.cartridge.setInteractionState('idle');
  }

  private beginCartridgeDrag(pointerId: number, point: THREE.Vector3): void {
    if (this.cartridgePhase !== 'loose' && this.cartridgePhase !== 'inserted') return;
    const cartridge = this.gamePackage.cartridge.root;
    if (this.cartridgePhase === 'inserted') this.removeInsertedCartridge();
    this.cartridgePhase = 'dragging';
    this.cartridgeProgress = 0;
    this.dragOffset.copy(cartridge.position).sub(point);
    this.dragOffset.y = 0;
    this.dragTargetPosition.copy(cartridge.position);
    this.dragTargetPosition.y = 0.76;
    this.dragTargetQuaternion.copy(cartridge.quaternion);
    this.gamePackage.cartridge.setInteractionState('selected');
    this.activePointers.set(pointerId, { kind: 'cartridge' });
    this.audio.play('pickup-cartridge');
    this.cameraRig.endOrbit();
  }

  private moveCartridge(point: THREE.Vector3): void {
    if (this.cartridgePhase !== 'dragging') return;
    this.dragTargetPosition.copy(point).add(this.dragOffset);
    this.dragTargetPosition.y = 0.76;
    this.dragTargetPosition.x = THREE.MathUtils.clamp(this.dragTargetPosition.x, -8.15, 8.15);
    this.dragTargetPosition.z = THREE.MathUtils.clamp(this.dragTargetPosition.z, -4.6, 4.8);

    const approach = this.poseCartridgeAt(this.gba.cartridge.alignmentSocket);
    const distance = this.dragTargetPosition.distanceTo(approach.position);
    if (distance < 2.15) {
      const strength = 1 - distance / 2.15;
      this.dragTargetQuaternion.slerp(approach.quaternion, 0.08 + strength * 0.18);
      if (distance < 1.15) {
        this.dragTargetPosition.x = THREE.MathUtils.lerp(
          this.dragTargetPosition.x,
          approach.position.x,
          strength * 0.22,
        );
        this.dragTargetPosition.z = THREE.MathUtils.lerp(
          this.dragTargetPosition.z,
          approach.position.z,
          strength * 0.22,
        );
        this.dragTargetPosition.y = THREE.MathUtils.lerp(
          this.dragTargetPosition.y,
          approach.position.y,
          strength * 0.32,
        );
      }
    }

    this.preventConsoleClipping(approach.position);
    this.updateInsertionAssessment();
  }

  private rotateCartridge(amount: number): void {
    if (this.cartridgePhase !== 'dragging') return;
    const rotation = new THREE.Quaternion().setFromAxisAngle(Y_AXIS, amount);
    this.dragTargetQuaternion.premultiply(rotation);
    this.updateInsertionAssessment();
  }

  private releaseCartridge(pointerId: number): void {
    const active = this.activePointers.get(pointerId);
    if (active?.kind !== 'cartridge') return;
    this.activePointers.delete(pointerId);
    if (this.cartCanInsert) {
      this.beginInsertion();
      return;
    }
    const cartridge = this.gamePackage.cartridge.root;
    this.dropStartY = cartridge.position.y;
    this.cartridgeProgress = 0;
    this.cartridgePhase = 'dropping';
    this.cartCanInsert = false;
    this.cartAssist = 0;
    this.audio.play('drop-cartridge');
  }

  private beginInsertion(): void {
    if (this.cartridgePhase !== 'dragging' && this.cartridgePhase !== 'loose') return;
    const cartridge = this.gamePackage.cartridge.root;
    this.capturePose(cartridge, this.cartStartPose);
    const seated = this.poseCartridgeAt(this.gba.cartridge.seatedSocket);
    this.cartEndPose.position.copy(seated.position);
    this.cartEndPose.quaternion.copy(seated.quaternion);
    this.cartEndPose.scale.copy(cartridge.scale);
    this.cartridgeProgress = 0;
    this.cartridgePhase = 'inserting';
    this.cartCanInsert = false;
    this.cartAssist = 1;
    this.gamePackage.cartridge.setInteractionState('selected');
  }

  private removeInsertedCartridge(): void {
    if (this.cartridgePhase !== 'inserted') return;
    const cartridge = this.gamePackage.cartridge.root;
    const approach = this.poseCartridgeAt(this.gba.cartridge.alignmentSocket);
    cartridge.position.copy(approach.position);
    cartridge.quaternion.copy(approach.quaternion);
    this.platformer.setCartridgeInserted(false);
    this.cartridgePhase = 'loose';
    this.gamePackage.cartridge.setInteractionState('idle');
    this.audio.play('pickup-cartridge');
    this.announce(
      this.powerOn
        ? 'Cartridge removed while powered on. The screen reports the missing cartridge.'
        : 'Cartridge removed.',
    );
  }

  private updateInsertionAssessment(): void {
    if (this.cartridgePhase !== 'dragging') {
      this.cartCanInsert = false;
      return;
    }
    const approach = this.poseCartridgeAt(this.gba.cartridge.alignmentSocket);
    const distance = this.dragTargetPosition.distanceTo(approach.position);
    const angle = this.dragTargetQuaternion.angleTo(approach.quaternion);
    this.cartAssist = clamp01(1 - distance / 2.15);
    // The full-size cartridge has a larger projected grab offset than the early
    // scale prototype. Keep the slot strict, but allow the visible center-to-slot
    // drag to enter the snap envelope after collision correction.
    this.cartCanInsert = distance <= 0.98
      && angle <= this.gba.cartridge.entryAngleToleranceRadians + 0.16;
  }

  private preventConsoleClipping(approachPosition: THREE.Vector3): void {
    if (this.dragTargetPosition.distanceTo(approachPosition) < 0.82) return;
    const scale = this.gbaRoot.scale.x;
    const halfX = 14.45 * scale * 0.5 + 1.05;
    const halfZ = 8.2 * scale * 0.5 + 0.62;
    const minX = this.gbaRoot.position.x - halfX;
    const maxX = this.gbaRoot.position.x + halfX;
    const minZ = this.gbaRoot.position.z - halfZ;
    const maxZ = this.gbaRoot.position.z + halfZ;
    if (
      this.dragTargetPosition.x <= minX
      || this.dragTargetPosition.x >= maxX
      || this.dragTargetPosition.z <= minZ
      || this.dragTargetPosition.z >= maxZ
    ) return;

    const distances = [
      { axis: 'x' as const, value: minX, distance: this.dragTargetPosition.x - minX },
      { axis: 'x' as const, value: maxX, distance: maxX - this.dragTargetPosition.x },
      { axis: 'z' as const, value: minZ, distance: this.dragTargetPosition.z - minZ },
      { axis: 'z' as const, value: maxZ, distance: maxZ - this.dragTargetPosition.z },
    ].sort((a, b) => a.distance - b.distance);
    const nearest = distances[0];
    this.dragTargetPosition[nearest.axis] = nearest.value;
  }

  private poseCartridgeAt(targetSocket: THREE.Object3D): Pose {
    targetSocket.updateWorldMatrix(true, false);
    const targetPosition = targetSocket.getWorldPosition(new THREE.Vector3());
    const targetQuaternion = targetSocket.getWorldQuaternion(new THREE.Quaternion());
    const localSocket = this.gamePackage.cartridge.alignmentSocket;
    const localQuaternionInverse = localSocket.quaternion.clone().invert();
    const rootQuaternion = targetQuaternion.clone().multiply(localQuaternionInverse);
    const rootScale = this.gamePackage.cartridge.root.scale.clone();
    const localOffset = localSocket.position.clone()
      .multiply(rootScale)
      .applyQuaternion(rootQuaternion);
    return {
      position: targetPosition.sub(localOffset),
      quaternion: rootQuaternion,
      scale: rootScale,
    };
  }

  private togglePower(): void {
    this.setPower(!this.powerOn);
  }

  private setPower(on: boolean): void {
    if (this.powerOn === on) return;
    this.powerOn = on;
    this.gba.setPowerState(on);
    this.platformer.setPowered(on);
    this.audio.play('power');
    if (!on) {
      this.audio.stopMusic();
      this.cameraRig.reset();
      this.announce('Console powered off.');
    } else {
      this.cameraRig.focusConsole(this.gbaRoot.position, false);
      this.announce('Console powering on.');
    }
  }

  private pressControl(control: GbaControlId, pointerId: number): void {
    if (control === 'power') {
      this.togglePower();
      this.activePointers.set(pointerId, { kind: 'control', control, source: `power:${pointerId}` });
      return;
    }
    const intent = CONTROL_TO_INTENT[control];
    const source = `gba:${control}:pointer:${pointerId}`;
    this.gba.setControlPressed(control, true);
    if (intent) this.platformer.setIntent(intent, true, source);
    this.activePointers.set(pointerId, { kind: 'control', control, source });
    this.audio.play('button');
  }

  private installControlHitTargets(): void {
    const sizes: Record<GbaControlId, THREE.Vector3Tuple> = {
      'dpad-up': [0.82, 0.86, 0.72],
      'dpad-down': [0.82, 0.86, 0.72],
      'dpad-left': [0.86, 0.82, 0.72],
      'dpad-right': [0.86, 0.82, 0.72],
      a: [1.34, 1.34, 0.78],
      b: [1.34, 1.34, 0.78],
      // Keep the two nearby menu targets disjoint. The previous generous boxes
      // overlapped in projection and could make Start resolve as Select.
      start: [0.52, 0.52, 0.62],
      select: [0.52, 0.52, 0.62],
      power: [1.28, 0.94, 0.92],
      l: [2.35, 0.75, 0.72],
      r: [2.35, 0.75, 0.72],
    };
    for (const [id, size] of Object.entries(sizes) as [GbaControlId, THREE.Vector3Tuple][]) {
      const target = new THREE.Mesh(new THREE.BoxGeometry(...size), this.controlHitMaterial);
      target.name = `hit-target:${id}`;
      target.position.z = 0.18;
      target.userData.controlId = id;
      target.userData.interaction = 'physical-control-hit-target';
      target.layers.set(1);
      target.castShadow = false;
      target.receiveShadow = false;
      this.gba.controls[id].pivot.add(target);
      this.gba.interactiveMeshes.push(target);
    }
  }

  private releasePointerControl(pointerId: number, active: Extract<ActivePointer, { kind: 'control' }>): void {
    this.activePointers.delete(pointerId);
    if (active.control === 'power') return;
    const intent = CONTROL_TO_INTENT[active.control];
    this.gba.setControlPressed(active.control, false);
    if (intent) this.platformer.setIntent(intent, false, active.source);
  }

  private handlePlatformerEvent(event: PlatformerEvent): void {
    const sound = SOUND_BY_PLATFORMER_EVENT[event.type];
    if (sound) this.audio.play(sound);
    if (event.type === 'game-start' || event.type === 'level-start' || event.type === 'resume') {
      this.audio.startMusic();
      this.cameraRig.focusConsole(this.gbaRoot.position, true);
    }
    if (
      event.type === 'power-off'
      || event.type === 'cartridge-removed'
      || event.type === 'death'
      || event.type === 'game-over'
      || event.type === 'victory'
    ) this.audio.stopMusic();
    if (event.type === 'pause') this.audio.stopMusic();
    if (event.type === 'no-cartridge') {
      this.announce('No game cartridge detected. Open the game case.');
    }
    if (event.type === 'game-start') this.announce('World 1 begins. The physical controls are active.');
    if (event.type === 'death') this.announce('Mario lost a life. Restarting from the checkpoint.');
    if (event.type === 'level-complete') this.announce('Level complete. Continuing to the next stage.');
  }

  private bindEvents(): void {
    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerCancel);
    this.canvas.addEventListener('pointerleave', this.onPointerLeave);
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    this.audioToggle.addEventListener('click', this.onAudioToggle);
    this.cameraReset.addEventListener('click', this.onCameraReset);
    this.helpToggle.addEventListener('click', this.onHelpToggle);
    this.helpClose.addEventListener('click', this.onHelpClose);
  }

  private unbindEvents(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerCancel);
    this.canvas.removeEventListener('pointerleave', this.onPointerLeave);
    this.canvas.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.audioToggle.removeEventListener('click', this.onAudioToggle);
    this.cameraReset.removeEventListener('click', this.onCameraReset);
    this.helpToggle.removeEventListener('click', this.onHelpToggle);
    this.helpClose.removeEventListener('click', this.onHelpClose);
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    void this.audio.unlock();
    this.canvas.focus({ preventScroll: true });
    this.updateRay(event.clientX, event.clientY);
    const target = this.resolvePickTarget();
    // Synthetic pointer streams and some cancelled touch gestures can have no
    // active pointer to capture. Real input still captures normally.
    try {
      this.canvas.setPointerCapture(event.pointerId);
    } catch {
      // Continue with the active-pointer map; pointerup/blur still releases it.
    }

    if (target.kind === 'control') {
      this.pressControl(target.control, event.pointerId);
      return;
    }
    if (target.kind === 'box' && this.boxPhase === 'closed') {
      this.openBox();
      return;
    }
    if (
      target.kind === 'cartridge'
      && (this.cartridgePhase === 'loose' || this.cartridgePhase === 'inserted')
    ) {
      const point = this.intersectDragPlane();
      if (point) this.beginCartridgeDrag(event.pointerId, point);
      return;
    }

    this.activePointers.set(event.pointerId, { kind: 'orbit' });
    this.cameraRig.beginOrbit(event.pointerId, event.clientX, event.clientY);
    this.canvas.classList.add('is-grabbing');
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    const active = this.activePointers.get(event.pointerId);
    if (active?.kind === 'orbit') {
      this.cameraRig.moveOrbit(event.pointerId, event.clientX, event.clientY);
      return;
    }
    if (active?.kind === 'cartridge') {
      this.updateRay(event.clientX, event.clientY);
      const point = this.intersectDragPlane();
      if (point) this.moveCartridge(point);
      return;
    }
    if (active?.kind === 'control') return;

    this.updateRay(event.clientX, event.clientY);
    this.setHovered(this.resolvePickTarget());
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    const active = this.activePointers.get(event.pointerId);
    if (active?.kind === 'control') this.releasePointerControl(event.pointerId, active);
    else if (active?.kind === 'cartridge') this.releaseCartridge(event.pointerId);
    else if (active?.kind === 'orbit') {
      this.activePointers.delete(event.pointerId);
      this.cameraRig.endOrbit(event.pointerId);
    }
    try {
      if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
    } catch {
      // A browser may already have cancelled capture during focus/viewport changes.
    }
    this.canvas.classList.remove('is-grabbing');
  };

  private readonly onPointerCancel = (event: PointerEvent): void => {
    this.onPointerUp(event);
  };

  private readonly onPointerLeave = (): void => {
    if (this.activePointers.size === 0) this.setHovered({ kind: 'none' });
  };

  private readonly onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    void this.audio.unlock();
    if (this.cartridgePhase === 'dragging') {
      this.rotateCartridge(event.deltaY * 0.0026);
    } else {
      this.cameraRig.zoom(event.deltaY);
    }
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    void this.audio.unlock();
    if (this.cartridgePhase === 'dragging' && (event.code === 'KeyQ' || event.code === 'KeyE')) {
      event.preventDefault();
      this.rotateCartridge(event.code === 'KeyQ' ? -0.16 : 0.16);
      return;
    }
    const control = KEY_TO_CONTROL[event.code];
    if (!control || this.pressedKeyboardControls.has(control)) return;
    this.pressedKeyboardControls.add(control);
    this.gba.setControlPressed(control, true);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    const control = KEY_TO_CONTROL[event.code];
    if (!control) return;
    this.pressedKeyboardControls.delete(control);
    if (![...this.activePointers.values()].some(
      (active) => active.kind === 'control' && active.control === control,
    )) this.gba.setControlPressed(control, false);
  };

  private readonly onBlur = (): void => {
    this.releaseAllInputs();
  };

  private readonly onAudioToggle = (): void => {
    const muted = !this.audio.isMuted();
    this.audio.setMuted(muted);
    this.audioToggle.textContent = muted ? 'Sound off' : 'Sound on';
    this.audioToggle.setAttribute('aria-pressed', String(muted));
  };

  private readonly onCameraReset = (): void => {
    this.cameraRig.reset();
  };

  private readonly onHelpToggle = (): void => {
    const opening = this.helpPanel.hidden;
    this.helpPanel.hidden = !opening;
    this.helpToggle.setAttribute('aria-expanded', String(opening));
  };

  private readonly onHelpClose = (): void => {
    this.helpPanel.hidden = true;
    this.helpToggle.setAttribute('aria-expanded', 'false');
    this.helpToggle.focus();
  };

  private releaseAllInputs(): void {
    for (const [pointerId, active] of [...this.activePointers]) {
      if (active.kind === 'control') this.releasePointerControl(pointerId, active);
      else if (active.kind === 'cartridge') this.releaseCartridge(pointerId);
      else this.cameraRig.endOrbit(pointerId);
    }
    this.activePointers.clear();
    for (const control of this.pressedKeyboardControls) this.gba.setControlPressed(control, false);
    this.pressedKeyboardControls.clear();
    this.canvas.classList.remove('is-grabbing');
  }

  private updateRay(clientX: number, clientY: number): void {
    const rect = this.canvas.getBoundingClientRect();
    this.pointerNdc.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
  }

  private intersectDragPlane(): THREE.Vector3 | null {
    return this.raycaster.ray.intersectPlane(this.dragPlane, new THREE.Vector3());
  }

  private resolvePickTarget(): PickTarget {
    const objects: THREE.Object3D[] = [
      ...this.gba.interactiveMeshes,
      this.gamePackage.cartridge.root,
      this.gamePackage.box.root,
    ];
    const hits = this.raycaster.intersectObjects(objects, true);
    for (const hit of hits) {
      const control = findControlId(hit.object);
      if (control) return { kind: 'control', control };
      if (
        isDescendant(hit.object, this.gamePackage.cartridge.root)
        && (this.cartridgePhase === 'loose' || this.cartridgePhase === 'inserted')
      ) return { kind: 'cartridge' };
      if (isDescendant(hit.object, this.gamePackage.box.root) && this.boxPhase === 'closed') {
        return { kind: 'box' };
      }
    }
    return { kind: 'none' };
  }

  private setHovered(target: PickTarget): void {
    const same = target.kind === this.hovered.kind
      && (target.kind !== 'control'
        || (this.hovered.kind === 'control' && target.control === this.hovered.control));
    if (same) return;
    if (this.hovered.kind === 'box') this.gamePackage.box.setHover(false);
    if (this.hovered.kind === 'cartridge' && this.cartridgePhase === 'loose') {
      this.gamePackage.cartridge.setInteractionState('idle');
    }
    this.hovered = target;
    if (target.kind === 'box') this.gamePackage.box.setHover(true);
    if (target.kind === 'cartridge' && this.cartridgePhase === 'loose') {
      this.gamePackage.cartridge.setInteractionState('hovered');
    }
    this.canvas.classList.toggle('is-interactive', target.kind !== 'none');
  }

  private updateUi(force = false): void {
    const platformerPhase = this.platformer.state;
    const signature = [
      this.powerOn,
      this.boxPhase,
      this.cartridgePhase,
      this.cartCanInsert,
      platformerPhase,
    ].join('|');
    if (!force && signature === this.lastUiSignature) return;
    this.lastUiSignature = signature;
    this.stateStrip.classList.toggle('is-on', this.powerOn);

    let label = this.powerOn ? 'Console on' : 'Console off';
    let message = 'Slide the power switch on the console';
    let pulse = false;

    if (!this.powerOn) {
      if (this.cartridgePhase === 'inserted') {
        label = 'Ready to play';
        message = 'Cartridge inserted - slide the power switch on';
        pulse = true;
      } else if (this.cartridgePhase === 'loose' || this.cartridgePhase === 'dropping') {
        label = 'Cartridge ready';
        message = 'Drag the cartridge to the rear slot - use Q/E or the wheel to rotate';
      }
    }

    if (this.boxPhase === 'opening') {
      label = 'Opening case';
      message = 'The hinged lid is opening';
    } else if (this.cartridgePhase === 'revealing') {
      label = 'Cartridge released';
      message = 'The cartridge is moving onto the table';
    } else if (this.cartridgePhase === 'dragging') {
      label = this.cartCanInsert ? 'Slot aligned' : 'Holding cartridge';
      message = this.cartCanInsert
        ? 'Aligned - release to insert'
        : 'Move to the rear slot - Q/E or wheel rotates the cartridge';
      pulse = this.cartCanInsert;
    } else if (this.cartridgePhase === 'inserting') {
      label = 'Inserting';
      message = 'Cartridge snapping into the console';
    }

    if (this.powerOn) {
      switch (platformerPhase) {
        case 'boot':
          label = 'Starting up';
          message = 'Startup sequence is playing inside the console screen';
          break;
        case 'no-cartridge':
          label = 'Cartridge missing';
          message = this.boxPhase === 'closed'
            ? 'Click the game case to open it'
            : this.cartridgePhase === 'loose'
              ? 'Drag the cartridge to the rear slot'
              : 'No cartridge detected';
          pulse = this.boxPhase === 'closed';
          break;
        case 'title':
          label = 'Game ready';
          message = 'Press the physical Start button or Enter';
          pulse = true;
          break;
        case 'playing':
          label = 'World in progress';
          message = 'D-pad move - A jumps - B runs - Start pauses';
          break;
        case 'paused':
          label = 'Paused';
          message = 'Press Start or Enter to resume';
          break;
        case 'dying':
          label = 'Life lost';
          message = 'Restarting from the last checkpoint';
          break;
        case 'level-complete':
          label = 'Level complete';
          message = 'Next stage loading';
          break;
        case 'game-over':
          label = 'Game over';
          message = 'Press Start to begin again';
          break;
        case 'victory':
          label = 'Campaign clear';
          message = 'Press Start to play again';
          break;
        case 'off':
          break;
      }
    }

    this.stateLabel.textContent = label;
    this.hint.textContent = message;
    this.hint.classList.toggle('is-pulsing', pulse);
    if (this.lastPlatformerPhase !== platformerPhase) {
      this.lastPlatformerPhase = platformerPhase;
      this.screenReaderStatus.textContent = `${label}. ${message}`;
    }
  }

  private announce(message: string): void {
    this.screenReaderStatus.textContent = message;
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      journey: {
        powerOn: this.powerOn,
        boxPhase: this.boxPhase,
        boxProgress: this.boxProgress,
        cartridgePhase: this.cartridgePhase,
        canInsert: this.cartCanInsert,
        orientationAssist: this.cartAssist,
      },
      platformer: this.platformer.getDiagnostics(),
      screen: {
        width: this.platformer.screenCanvas.width,
        height: this.platformer.screenCanvas.height,
        frameVersion: this.platformer.frameVersion,
        textureFilter: 'nearest',
        integratedCanvasTexture: true,
      },
      renderer: {
        fps: this.fps,
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, 2),
      },
      targets: this.getProjectedTargets(),
    };
  }

  private getProjectedTargets(): Record<string, ProjectedPoint> {
    const targets: Record<string, THREE.Object3D> = {
      power: this.gba.controls.power.pivot,
      box: this.gamePackage.box.lidPivot,
      cartridge: this.gamePackage.cartridge.pickupSocket,
      slot: this.gba.cartridge.alignmentSocket,
      dpadLeft: this.gba.controls['dpad-left'].pivot,
      dpadRight: this.gba.controls['dpad-right'].pivot,
      a: this.gba.controls.a.pivot,
      b: this.gba.controls.b.pivot,
      start: this.gba.controls.start.pivot,
      select: this.gba.controls.select.pivot,
      screen: this.gba.screenMesh,
    };
    const result: Record<string, ProjectedPoint> = {};
    for (const [id, object] of Object.entries(targets)) result[id] = this.projectObject(object);
    return result;
  }

  private projectObject(object: THREE.Object3D): ProjectedPoint {
    const point = object.getWorldPosition(new THREE.Vector3()).project(this.camera);
    return {
      x: (point.x * 0.5 + 0.5) * this.canvas.clientWidth,
      y: (-point.y * 0.5 + 0.5) * this.canvas.clientHeight,
      visible: point.z >= -1 && point.z <= 1 && Math.abs(point.x) <= 1.1 && Math.abs(point.y) <= 1.1,
    };
  }

  private installTestApi(): void {
    window.__POCKET_ARCHIVE_TEST__ = {
      setPower: (on) => this.setPower(on),
      openBox: () => this.openBox(),
      revealCartridge: () => this.revealCartridgeImmediate(),
      placeCartridgeAtSlot: () => {
        if (this.cartridgePhase === 'cradle') this.revealCartridgeImmediate();
        if (this.cartridgePhase === 'inserted') this.removeInsertedCartridge();
        const pose = this.poseCartridgeAt(this.gba.cartridge.alignmentSocket);
        const cartridge = this.gamePackage.cartridge.root;
        cartridge.position.copy(pose.position);
        cartridge.quaternion.copy(pose.quaternion);
        cartridge.scale.copy(pose.scale);
        this.dragTargetPosition.copy(pose.position);
        this.dragTargetQuaternion.copy(pose.quaternion);
        this.cartridgePhase = 'loose';
        this.cartCanInsert = true;
      },
      insertCartridge: () => {
        if (this.cartridgePhase === 'cradle') this.revealCartridgeImmediate();
        if (this.cartridgePhase === 'inserted') return;
        const pose = this.poseCartridgeAt(this.gba.cartridge.alignmentSocket);
        const cartridge = this.gamePackage.cartridge.root;
        cartridge.position.copy(pose.position);
        cartridge.quaternion.copy(pose.quaternion);
        cartridge.scale.copy(pose.scale);
        this.cartridgePhase = 'loose';
        this.beginInsertion();
      },
      removeCartridge: () => this.removeInsertedCartridge(),
      setIntent: (intent, pressed, source = 'qa') => this.platformer.setIntent(intent, pressed, source),
      pulseIntent: (intent) => this.platformer.pulseIntent(intent, 'qa-pulse'),
      advancePlatformer: (stepCount) => {
        this.platformer.advanceFixedSteps(stepCount, true);
        if (this.platformer.consumeTextureDirty()) this.screenTexture.needsUpdate = true;
        this.publishDiagnostics();
      },
      getScreenCanvas: () => this.platformer.screenCanvas,
      getTargets: () => this.getProjectedTargets(),
    };
  }

  private capturePose(object: THREE.Object3D, target: Pose): void {
    target.position.copy(object.position);
    target.quaternion.copy(object.quaternion);
    target.scale.copy(object.scale);
  }

  private disposeObject(root: THREE.Object3D): void {
    const materials = new Set<THREE.Material>();
    root.traverse((object) => {
      if (!(object instanceof THREE.Mesh) && !(object instanceof THREE.InstancedMesh)) return;
      object.geometry.dispose();
      const source = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of source) materials.add(material);
    });
    for (const material of materials) {
      const values = Object.values(material) as unknown[];
      for (const value of values) if (value instanceof THREE.Texture) value.dispose();
      material.dispose();
    }
  }

  private getElement<T extends HTMLElement = HTMLElement>(selector: string): T {
    const element = document.querySelector<T>(selector);
    if (!element) throw new Error(`Missing interface element: ${selector}`);
    return element;
  }
}
